import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AssessmentsService, type AssessmentVariant } from "@/lib/api_client";
import { useAssessmentVariantMetadataList, useCreateAssessmentVariantMetadata, useUpdateAssessmentVariantMetadata } from "@/hooks/api";
import { fetchAll } from "@/hooks/api/fetchAll";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const VARIANT_NAMES: Record<string, string> = {
  hearthealth: "Hjertehelse",
  stress: "Stress",
  fitness: "Fitness",
  bodyComposition: "Kroppssammensetning",
  sleep: "Søvn",
  other: "Annet",
  emphasizedBiometrics: "Viktige biomarkører",
  reccomendations: "Anbefalinger",
  summary: "Oppsummering",
};

export function VariantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: variants, isLoading: variantLoading } = useQuery({
    queryKey: ["assessment-variants"],
    queryFn: () => fetchAll<AssessmentVariant>(AssessmentsService.getApiAssessmentVariants),
    staleTime: 10 * 60_000,
  });
  const { data: metadataList, isLoading: metadataLoading } = useAssessmentVariantMetadataList();
  const createMetadata = useCreateAssessmentVariantMetadata();
  const updateMetadata = useUpdateAssessmentVariantMetadata();

  const variant = variants?.find((v) => v.id === id);
  const metadata = metadataList?.find((m) => m.assessmentVariantId === id);

  // Edited fields (empty = unchanged from fetched)
  const [editedPrompt, setEditedPrompt] = useState("");
  const [editedClinicalModel, setEditedClinicalModel] = useState("");
  const [editedK, setEditedK] = useState("");
  const [editedTemperature, setEditedTemperature] = useState("");
  const [editedMaxTokens, setEditedMaxTokens] = useState("");
  const [editedModel, setEditedModel] = useState("");
  const [editedProvider, setEditedProvider] = useState("");

  const hasChanges = !!editedPrompt || !!editedClinicalModel || !!editedK || !!editedTemperature || !!editedMaxTokens || !!editedModel || !!editedProvider;

  const currentPrompt = editedPrompt || metadata?.prompt || "";
  const currentClinicalModel = editedClinicalModel || metadata?.clinicalModel || "";
  const currentK = editedK || metadata?.k?.toString() || "";
  const currentTemperature = editedTemperature || metadata?.temperature?.toString() || "";
  const currentMaxTokens = editedMaxTokens || metadata?.maxTokens?.toString() || "";
  const currentModel = editedModel || metadata?.model || "";
  const currentProvider = editedProvider || metadata?.provider || "";

  const handleSave = async () => {
    try {
      const data: Record<string, unknown> = {};
      if (editedPrompt) data.prompt = editedPrompt;
      if (editedClinicalModel) data.clinicalModel = editedClinicalModel;
      if (editedK) data.k = editedK;
      if (editedTemperature) data.temperature = editedTemperature;
      if (editedMaxTokens) data.maxTokens = parseInt(editedMaxTokens);
      if (editedModel) data.model = editedModel;
      if (editedProvider) data.provider = editedProvider;

      if (metadata?.id) {
        await updateMetadata.mutateAsync({ id: metadata.id, data: data as any });
      } else {
        await createMetadata.mutateAsync({ assessmentVariantId: id!, ...data } as any);
      }

      toast.success("Innstillinger lagret");
      setEditedPrompt("");
      setEditedClinicalModel("");
      setEditedK("");
      setEditedTemperature("");
      setEditedMaxTokens("");
      setEditedModel("");
      setEditedProvider("");
    } catch (err) {
      toast.error("Kunne ikke lagre innstillinger");
    }
  };

  const handleCancel = () => {
    setEditedPrompt("");
    setEditedClinicalModel("");
    setEditedK("");
    setEditedTemperature("");
    setEditedMaxTokens("");
    setEditedModel("");
    setEditedProvider("");
  };

  const isLoading = variantLoading || metadataLoading;
  const isPending = createMetadata.isPending || updateMetadata.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  if (!variant) {
    return <p className="text-admin-text-subtle p-4">Variant ikke funnet</p>;
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/assessment-variants")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">
          {VARIANT_NAMES[variant.variant] ?? variant.variant}
        </h2>
        <span className="text-sm text-admin-text-subtle">{variant.variant}</span>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          {/* AI Parameters */}
          <h3 className="text-sm font-medium text-admin-text-primary">AI Parametere</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-admin-text-primary">K (Top K)</label>
              <Input value={currentK} onChange={(e) => setEditedK(e.target.value)} placeholder="f.eks. 40" className="h-8 text-sm mt-1" />
              <p className="text-[10px] text-admin-text-subtle mt-0.5">Sampling diversity</p>
            </div>
            <div>
              <label className="text-xs font-medium text-admin-text-primary">Temperature</label>
              <Input type="number" step="0.1" min="0" max="2" value={currentTemperature} onChange={(e) => setEditedTemperature(e.target.value)} placeholder="f.eks. 0.7" className="h-8 text-sm mt-1" />
              <p className="text-[10px] text-admin-text-subtle mt-0.5">0 = focused, 2 = creative</p>
            </div>
            <div>
              <label className="text-xs font-medium text-admin-text-primary">Max Tokens</label>
              <Input type="number" value={currentMaxTokens} onChange={(e) => setEditedMaxTokens(e.target.value)} placeholder="f.eks. 1000" className="h-8 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-admin-text-primary">Model</label>
              <Input value={currentModel} onChange={(e) => setEditedModel(e.target.value)} placeholder="f.eks. gpt-4" className="h-8 text-sm mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-admin-text-primary">Provider</label>
              <Input value={currentProvider} onChange={(e) => setEditedProvider(e.target.value)} placeholder="f.eks. openai" className="h-8 text-sm mt-1" />
            </div>
          </div>

          <hr className="border-admin-card-border" />

          {/* Clinical Model */}
          <div>
            <label className="text-xs font-medium text-admin-text-primary">Klinisk Modell</label>
            <p className="text-[10px] text-admin-text-subtle mb-1">{metadata?.clinicalModel ? "Definert" : "Ikke definert"}</p>
            <Textarea
              value={currentClinicalModel}
              onChange={(e) => setEditedClinicalModel(e.target.value)}
              placeholder="Ingen klinisk modell definert"
              className="min-h-[150px] text-sm"
            />
          </div>

          <hr className="border-admin-card-border" />

          {/* Prompt */}
          <div>
            <label className="text-xs font-medium text-admin-text-primary">Prompt</label>
            <p className="text-[10px] text-admin-text-subtle mb-1">{metadata?.prompt ? "Definert" : "Ikke definert"}</p>
            <Textarea
              value={currentPrompt}
              onChange={(e) => setEditedPrompt(e.target.value)}
              placeholder="Ingen prompt definert"
              className="min-h-[200px] text-sm"
            />
          </div>

          {/* Save/Cancel */}
          {hasChanges && (
            <div className="flex gap-2">
              <Button size="sm" disabled={isPending} onClick={handleSave}>
                {isPending ? "Lagrer..." : "Lagre endringer"}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>Avbryt</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
